import React, { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';

/**
 * Dialog component using Radix UI
 * @param {Object} props - Component props
 * @returns {JSX.Element} RadixDialog component
 */
const RadixDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  maxWidth = 'md',
  showCloseButton = true,
  onCloseAutoFocus,
  ...props
}) => {
  // Add/remove body class to prevent scrolling when dialog is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [open]);
  // Max width classes
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    full: 'max-w-full',
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={true} {...props}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 radix-dialog-overlay" />
        <Dialog.Content onCloseAutoFocus={onCloseAutoFocus} className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 z-[60] w-full ${maxWidthClasses[maxWidth]} max-h-[85vh] overflow-auto radix-dialog-content`}>
          {showCloseButton && (
            <Dialog.Close aria-label="Close" className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100">
              <Cross2Icon className="w-4 h-4 text-gray-500" />
            </Dialog.Close>
          )}

          {title && (
            <Dialog.Title className="text-xl font-semibold text-gray-900 mb-2">
              {title}
            </Dialog.Title>
          )}

          {description && (
            <Dialog.Description className="text-gray-600 mb-4">
              {description}
            </Dialog.Description>
          )}

          <div className="py-2">
            {children}
          </div>

          {footer && (
            <div className="mt-6 flex justify-end gap-3">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RadixDialog;
